import React, { useEffect, useRef, useState } from "react";
import {
  ChevronRight,
  Mail,
  MapPin,
  Phone,
  Send,
  ShieldCheck,
  Handshake,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const ContactPage = () => {
  const pageRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    honeypot: "",
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", { opacity: 0, y: 40, duration: 0.9, delay: 0.25 });
      gsap.from(".contact-card", { opacity: 0, y: 60, duration: 1, stagger: 0.12 });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const submit = async (e) => {
    e.preventDefault();

    try {
      await api.post("/contact", form);
      toast.success("Message sent successfully");

      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        honeypot: "",
      });
    } catch (err) {
      toast.error(err.cleanMessage || "Message failed to send");
    }
  };

  return (
    <main ref={pageRef}>
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img src={HERO_IMAGE} alt="Contact AKSUBEB" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />
          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Get In Touch
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Contact Us
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Get in touch with Akwa Ibom State Universal Basic Education Board.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Contact Us</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-20">
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Link
              to="/complaints"
              className="contact-card group bg-white rounded-[30px] p-7 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-5 group-hover:bg-[#ff6600] group-hover:text-white transition-all">
                <ShieldCheck size={26} />
              </div>

              <h3 className="text-2xl font-black text-[#06183f]" style={{ fontFamily: "'Alegreya Sans', sans-serif" }}>
                Citizen Feedback & Accountability Desk
              </h3>

              <p className="text-slate-600 mt-3 leading-relaxed">
                Submit school-related complaints, project concerns, teacher conduct reports, infrastructure issues, illegal levy reports and pupil welfare feedback securely.
              </p>

              <span className="inline-flex items-center gap-2 mt-5 text-[#06183f] font-bold border-b-2 border-[#06183f] pb-1 group-hover:text-[#ff6600] group-hover:border-[#ff6600]">
                Submit feedback <ArrowRight size={18} />
              </span>
            </Link>

            <Link
              to="/partnership"
              className="contact-card group bg-white rounded-[30px] p-7 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center mb-5 group-hover:bg-[#06183f] group-hover:text-white transition-all">
                <Handshake size={26} />
              </div>

              <h3 className="text-2xl font-black text-[#06183f]" style={{ fontFamily: "'Alegreya Sans', sans-serif" }}>
                Education Partnership Desk
              </h3>

              <p className="text-slate-600 mt-3 leading-relaxed">
                Partner with AKSUBEB on school development, digital learning, classroom support, teacher training, infrastructure, welfare and community education initiatives.
              </p>

              <span className="inline-flex items-center gap-2 mt-5 text-[#06183f] font-bold border-b-2 border-[#06183f] pb-1 group-hover:text-[#ff6600] group-hover:border-[#ff6600]">
                Partner with us <ArrowRight size={18} />
              </span>
            </Link>
          </div>

          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8">
            <div className="space-y-5">
              {[
                {
                  icon: MapPin,
                  title: "Office Address",
                  text: "Idongesit Nkanga Secretariat, Uyo 520101, Akwa Ibom State, Nigeria",
                },
                {
                  icon: Mail,
                  title: "Email",
                  text: "info@aksubeb.gov.ng",
                },
                {
                  icon: Phone,
                  title: "Phone",
                  text: "+2347025328036",
                },
              ].map(({ icon: Icon, title, text }) => (
                <div key={title} className="contact-card bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)]">
                  <div className="w-12 h-12 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>

                  <h3 className="font-black text-[#06183f]" style={{ fontFamily: "'Alegreya Sans', sans-serif" }}>
                    {title}
                  </h3>

                  <p className="text-slate-600 mt-2 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            <form onSubmit={submit} className="contact-card bg-white rounded-[34px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] p-6 md:p-8">
              <input type="text" value={form.honeypot} onChange={(e) => setForm({ ...form, honeypot: e.target.value })} className="hidden" tabIndex="-1" autoComplete="off" />

              <div className="grid md:grid-cols-2 gap-4">
                <input required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
                <input required type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
                <input placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
                <input required placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
              </div>

              <textarea required rows="6" placeholder="Message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-4 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />

              <button className="mt-5 inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-[#06183f] hover:bg-[#ff6600] text-white font-black transition-colors">
                Send Message <Send size={18} />
              </button>
            </form>
          </div>

          <div className="mt-10">
            <iframe
              title="AKSUBEB Map"
              className="w-full h-[430px] rounded-[34px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)]"
              loading="lazy"
              src="https://www.google.com/maps?q=Idongesit%20Nkanga%20Secretariat%20Uyo%20Akwa%20Ibom%20State%20Nigeria&output=embed"
            />
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;